/* eslint-disable import/no-cycle */
import Obj from './Obj.js';
import Utils from '../Utils.js';
import Access from './InterfaceReducer.js';
import Constants from '../Constants.js';
import InterfaceActions from './InterfaceActions.js';
import Can from './Can.js';
const {
  getId
} = Utils;

/**
 * @Class Grp - Class representing a group of items
 */
class Grp {
  /**
   * creates a group
   * @param {string} parentPath the path of the api call for the immediate parent
   * @param {string} id identifier for the group to be queried for
   * @param {string} attribute additional information to be added to the path
   */
  constructor(parentPath, id, attribute) {
    this.id = getId(id);
    this.attribute = attribute;
    this.parentPath = parentPath;
    this.path = `${parentPath}/${this.id}/${this.attribute}`;
  }
  can() {
    return Can.create(this.path, false, true);
  }

  /**
   * makes an api call for the list of objects
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  list(options, queryName = null) {
    /**
     * @returns {Promise} represents the result of the api call for the list
     */
    return InterfaceActions.list(this.path, this.id, Constants.INTERFACE_LIST, options, queryName);
  }

  /**
   * api call to search for items as a child of the current path
   * @param {object} obj search to execute
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  search(obj, options, queryName = null) {
    /**
     * @returns {Promise} represents the result of the api call to create the item
     */
    return InterfaceActions.search(this.path, obj, this.id, Constants.INTERFACE_LIST, options, queryName);
  }

  /**
   * api call to create an item as a child of the current path
   * @param {object} obj item to be created
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  create(obj, options) {
    /**
     * @returns {Promise} represents the result of the api call to create the item
     */
    return InterfaceActions.create(this.path, obj, this.id, Constants.INTERFACE_CREATE_ADD, options);
  }

  /**
   * api call to get a child of the current path
   * @param {string} id id of the item to be returned
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  get(id, options, queryName = null) {
    /**
     * @returns {Promise} represents the result of the api call to get the item
     */
    return this.obj(id).get(options, queryName);
  }

  /**
   * Returns a action to perform a getDeviceByNetworkAlias
   */
  getDeviceByNetworkAlias(network, key, value, options) {
    return InterfaceActions.getDeviceByNetworkAlias(this.path, network, key, value, options);
  }

  /**
   * makes an api call to get the history of an object at the path
   * @param {object} query.query query params that the server will use
   * @param {string} query.queryName query identifier
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  history(query, options) {
    return InterfaceActions.getHistory(`${this.path}.history`, this.parentPath, {
      ...options,
      q: JSON.stringify(query.query)
    }, query.queryName);
  }
  events(query, options) {
    return InterfaceActions.getHistory(`${this.path}.events`, this.parentPath, {
      ...options,
      q: JSON.stringify(query.query)
    }, query.queryName);
  }

  /**
   * api call to update a child of the current path
   * @param {string} id id of the item to be updated
   * @param {object} data values of the item to be changed
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  update(id, data, options) {
    /**
     * @returns {Promise} represents the result of the api call to update the item
     */
    return this.obj(id).update(data, options); // InterfaceActions.updateListObject( this.path, id, data, options )
  }

  /**
   * api call to delete a child of the current path
   * @param {string} id id of the item to be deleted
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  delete(id, body, options) {
    /**
     * @returns {Promise} represents the result of the api call to delete the item
     */
    return this.obj(id).delete(body, options); // InterfaceActions.deleteRelationship( this.path, id, options )
  }

  /**
   * api call to get the model for the path
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  model(options) {
    /**
     * @returns {Promise} represents the result of the api call to get the model
     */
    return InterfaceActions.get(`${this.path}.model`, this.isInGroup ? this.parentPath : null, options);
  }

  /**
   * locally filters the results
   * @param {object} filter the filter object for the path
   */
  filter(filter) {
    return {
      type: Constants.INTERFACE_FILTER,
      filter,
      path: this.path
    };
  }

  /**
   * returns an instance of the obj class with the current path prepended
   * @param {string} id
   * @return {Obj} has the current path with the id
   */
  obj(id) {
    return Obj.create(this.path, id, true);
  }

  // Local Data Access
  /**
   * @param {object} state the current redux state
   * @param {object} opts
   * @returns {object} results, in the form of a list
   */
  getList(state, queryName) {
    return Access.getList(state, this.path, queryName);
  }

  /**
   * @param {object} state current redux state
   * @param {object} opts
   * @returns {object} table of the results
   */
  getTable(state, opts, queryName) {
    return Access.getTable(state, this.path, opts, queryName);
  }

  /**
   * gets the results of a specific query
   * @param {object} state the current redux state
   * @param {string} queryName identifier of the query
   * @returns {object} the results of the query
   */
  getHistory(state, queryName, opts) {
    return Access.getHistory(state, `${this.path}.history`, queryName, opts);
  }
  getEvents(state, queryName, opts) {
    return Access.getHistory(state, `${this.path}.events`, queryName, opts);
  }

  /**
   * @param {object} state the current redux state
   * @param {object} opts
   * @returns {boolean} whether or not the results can be returned as a table
   */
  isTable(state, opts, queryName) {
    return Access.isTable(state, this.path, opts, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @param {string} id id of the object
   * @param {object} opts
   * @returns {object} returns the resulting object from the query
   */
  getObject(state, id, queryName) {
    return this.obj(id).getObject(state, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {object} returns a model for the query
   */
  getModel(state, queryName) {
    return Access.getObject(state, `${this.path}.model`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is loading
   */
  isLoading(state, queryName) {
    return Access.isLoading(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query had an error
   */
  isError(state, queryName) {
    return Access.isError(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is finished
   */
  isDone(state, queryName) {
    return Access.isDone(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {Error} an error if one exists, otherwise undefined
   */
  getError(state, queryName) {
    return Access.getError(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is loading
   */
  isHistoryLoading(state, queryName) {
    return Access.isLoading(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query had an error
   */
  isHistoryError(state, queryName) {
    return Access.isError(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is finished
   */
  isHistoryDone(state, queryName) {
    return Access.isDone(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {Error} an error if one exists, otherwise undefined
   */
  getHistoryError(state, queryName) {
    return Access.getError(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is loading
   */
  isEventsLoading(state, queryName) {
    return Access.isLoading(state, `${this.path}.events`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query had an error
   */
  isEventsError(state, queryName) {
    return Access.isError(state, `${this.path}.events`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is finished
   */
  isEventsDone(state, queryName) {
    return Access.isDone(state, `${this.path}.events`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {Error} an error if one exists, otherwise undefined
   */
  getEventsError(state, queryName) {
    return Access.getError(state, `${this.path}.events`, queryName);
  }
}
function createGrp(path, id, attr) {
  return new Grp(path, id, attr);
}
export default {
  create: createGrp
};